import fadouaImg from "@/app/blog/_assets/images/authors/fadoua.jpeg";
import fluentCityImg from "@/public/blog2/article1/fluent-city-spanish.jpg";
import deWorkbookImg from "@/public/blog2/article2/de.jpg";
import frWorkbookImg from "@/public/blog2/article3/fr.jpg";
import enWorkbookImg from "@/public/blog2/article4/en.jpg";
import type { StaticImageData } from "next/image";
import type { JSX } from "react";
import Article1 from "../posts/article1";
import Article2 from "../posts/article2";
import Article3 from "../posts/article3";
import Article4 from "../posts/article4";

// ==================================================================================================================================================================
// BLOG CATEGORIES 🏷️
// ==================================================================================================================================================================

export type categoryType = {
  slug: string;
  title: string;
  titleShort?: string;
  description: string;
  descriptionShort?: string;
};

// These slugs are used to generate pages in the /blog/category/[categoryI].js. It's a way to group articles by category.
const categorySlugs: { [key: string]: string } = {
  learning: "learning",
  language: "language",
};

// All the blog categories data display in the /blog/category/[categoryI].js pages.
export const categories: categoryType[] = [
  {
    // The slug to use in the URL, from the categorySlugs object above.
    slug: categorySlugs.learning,
    // The title to display the category title (h1), the category badge, the category filter, and more. Less than 60 characters.
    title: "learnings",
    // A short version of the title above, display in small components like badges. 1 or 2 words
    titleShort: "Learning",
    // The description of the category to display in the category page. Up to 160 characters.
    description:
      "Here are the latest features we've added to Linpoly. We're constantly improving our product to help you learn better.",
    // A short version of the description above, only displayed in the <Header /> on mobile. Up to 60 characters.
    descriptionShort: "Latest features added to Linpoly.",
  },
  {
    slug: categorySlugs.language,
    title: "How Tos & Tutorials",
    titleShort: "Languages",
    description:
      "Learn how to use Linpoly with these step-by-step tutorials. I'll show you how to learn faster and save time.",
    descriptionShort:
      "Learn how to use Linpoly with these step-by-step tutorials.",
  },
];

// ==================================================================================================================================================================
// BLOG AUTHORS 📝
// ==================================================================================================================================================================

export type authorType = {
  slug: string;
  name: string;
  job: string;
  description: string;
  avatar: StaticImageData | string;
  socials?: {
    name: string;
    icon: JSX.Element;
    url: string;
  }[];
};

// Social icons used in the author's bio.
const socialIcons: {
  [key: string]: {
    name: string;
    svg: JSX.Element;
  };
} = {
  twitter: {
    name: "Twitter",
    svg: (
      <svg
        version="1.1"
        id="svg5"
        x="0px"
        y="0px"
        viewBox="0 0 1668.56 1221.19"
        className="w-9 h-9"
        // Using a dark theme? ->  className="w-9 h-9 fill-white"
      >
        <g id="layer1" transform="translate(52.390088,-25.058597)">
          <path
            id="path1009"
            d="M283.94,167.31l386.39,516.64L281.5,1104h87.51l340.42-367.76L984.48,1104h297.8L874.15,558.3l361.92-390.99   h-87.51l-313.51,338.7l-253.31-338.7H283.94z M412.63,231.77h136.81l604.13,807.76h-136.81L412.63,231.77z"
          />
        </g>
      </svg>
    ),
  },
  linkedin: {
    name: "LinkedIn",
    svg: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-6 h-6"
        // Using a dark theme? ->  className="w-6 h-6 fill-white"
        viewBox="0 0 24 24"
      >
        <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
      </svg>
    ),
  },
  github: {
    name: "GitHub",
    svg: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-6 h-6"
        // Using a dark theme? ->  className="w-6 h-6 fill-white"
        viewBox="0 0 24 24"
      >
        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
      </svg>
    ),
  },
};

// These slugs are used to generate pages in the /blog/author/[authorId].js. It's a way to show all articles from an author.
const authorSlugs: {
  [key: string]: string;
} = {
  omar: "omar",
  fadoua: "fadoua",
};

// All the blog authors data display in the /blog/author/[authorId].js pages.
export const authors: authorType[] = [
  {
    // The slug to use in the URL, from the authorSlugs object above.
    slug: authorSlugs.omar,
    // The name to display in the author's bio. Up to 60 characters.
    name: "Omar Achour",
    // The job to display in the author's bio. Up to 60 characters.
    job: "Maker of Rewindr",
    // The description of the author to display in the author's bio. Up to 160 characters.
    description:
      "Omar is a devops and an entrepreneur. He's currently building Rewindr, the #1 Shopify social proof tool.",
    // The avatar of the author to display in the author's bio and avatar badge. It's better to use a local image, but you can also use an external image (https://...)
    avatar: fadouaImg,
    // A list of social links to display in the author's bio.
    socials: [
      {
        name: socialIcons.twitter.name,
        icon: socialIcons.twitter.svg,
        url: "https://twitter.com/oachoor",
      },
      {
        name: socialIcons.linkedin.name,
        icon: socialIcons.linkedin.svg,
        url: "https://www.linkedin.com/in/oachoor/",
      },
      {
        name: socialIcons.github.name,
        icon: socialIcons.github.svg,
        url: "https://github.com/oachoor",
      },
    ],
  },
  {
    // The slug to use in the URL, from the authorSlugs object above.
    slug: authorSlugs.fadoua,
    // The name to display in the author's bio. Up to 60 characters.
    name: "Fadoua",
    // The job to display in the author's bio. Up to 60 characters.
    job: "Maker of Linpoly",
    // The description of the author to display in the author's bio. Up to 160 characters.
    description:
      "Fadoua is a tutor and an entrepreneur. She's currently building Linpoly.",
    // The avatar of the author to display in the author's bio and avatar badge. It's better to use a local image, but you can also use an external image (https://...)
    avatar: fadouaImg,
    // A list of social links to display in the author's bio.
    socials: [
      {
        name: socialIcons.linkedin.name,
        icon: socialIcons.linkedin.svg,
        url: "https://www.linkedin.com/in/fadoua-louhaichi",
      },
    ],
  },
];

// ==================================================================================================================================================================
// BLOG ARTICLES 📚
// ==================================================================================================================================================================

export type articleType = {
  slug: string;
  title: string;
  description: string;
  categories: categoryType[];
  author: authorType;
  publishedAt: string;
  image: {
    src?: StaticImageData;
    urlRelative: string;
    alt: string;
  };
  locale: string;
  content: JSX.Element;
};

// These styles are used in the content of the articles. When you update them, all articles will be updated.
export const styles: {
  [key: string]: string;
} = {
  h2: "text-2xl lg:text-4xl font-bold tracking-tight mb-4 text-base-content",
  h3: "text-xl lg:text-2xl font-bold tracking-tight mb-2 text-base-content",
  p: "text-base-content/90 leading-relaxed",
  ul: "list-inside list-disc text-base-content/90 leading-relaxed",
  li: "list-item",
  // Altnernatively, you can use the library react-syntax-highlighter to display code snippets.
  code: "text-sm font-mono bg-neutral text-neutral-content p-6 rounded-box my-4 overflow-x-scroll select-all",
  codeInline:
    "text-sm font-mono bg-base-300 px-1 py-0.5 rounded-box select-all",
};

// All the blog articles data display in the /blog/[articleId].js pages.
export const articles: articleType[] = [
  {
    // The unique slug to use in the URL. It's also used to generate the canonical URL.
    slug: "how-ai-empowers-learners-tutors-instant-feedback",
    // The title to display in the article page (h1). Less than 60 characters. It's also used to generate the meta title.
    title: "How AI Empowers Learners and Tutors with Instant Feedback",
    // The description of the article to display in the article page. Up to 160 characters. It's also used to generate the meta description.
    description:
      "Artificial Intelligence (AI) has dramatically transformed various aspects of our lives, and language learning is no exception. With AI's ability to understand, process, and even emulate human languages, it has become an indispensable tool for learners, tutors, and educators alike.",
    // An array of categories of the article. It's used to generate the category badges, the category filter, and more.
    categories: [
      categories.find((category) => category.slug === categorySlugs.learning),
    ],
    // The author of the article. It's used to generate a link to the author's bio page.
    author: authors.find((author) => author.slug === authorSlugs.fadoua),
    // The date of the article. It's used to generate the meta date.
    publishedAt: "2024-09-20",
    image: {
      // The image to display in <CardArticle /> components.
      src: fluentCityImg,
      // The relative URL of the same image to use in the Open Graph meta tags & the Schema Markup JSON-LD. It should be the same image as the src above.
      urlRelative: "/blog2/article1/fluent-city-spanish.jpg",
      alt: "Fluent City Spanish",
    },
    locale: "en_US",
    // The actual content of the article that will be shown under the <h1> title in the article page.
    content: <Article1 />,
  },
  {
    // The unique slug to use in the URL. It's also used to generate the canonical URL.
    slug: "pruefungsvorbereitung-fuer-b1",
    // The title to display in the article page (h1). Less than 60 characters. It's also used to generate the meta title.
    title: "Prüfungsvorbereitung für B1",
    // The description of the article to display in the article page. Up to 160 characters. It's also used to generate the meta description.
    description:
      "Die Vorbereitung auf die B1-Prüfung ist eine spannende Herausforderung. Ob du das Goethe-Zertifikat, den TestDaF oder TELC anstrebst – eine gute Vorbereitung ist entscheidend für den Erfolg. Hier erhältst du wichtige Tipps und erfährst, wie ein kostenloses Arbeitsbuch dich optimal unterstützt.",
    // An array of categories of the article. It's used to generate the category badges, the category filter, and more.
    categories: [
      categories.find((category) => category.slug === categorySlugs.learning),
    ],
    // The author of the article. It's used to generate a link to the author's bio page.
    author: authors.find((author) => author.slug === authorSlugs.fadoua),
    // The date of the article. It's used to generate the meta date.
    publishedAt: "2024-10-12",
    image: {
      // The image to display in <CardArticle /> components.
      src: deWorkbookImg,
      // The relative URL of the same image to use in the Open Graph meta tags & the Schema Markup JSON-LD. It should be the same image as the src above.
      urlRelative: "/blog2/article2/de.jpg",
      alt: "Fluent City Spanish",
    },
    locale: "de_DE",
    // The actual content of the article that will be shown under the <h1> title in the article page.
    content: <Article2 />,
  },
  {
    // The unique slug to use in the URL. It's also used to generate the canonical URL.
    slug: "se-preparer-aux-examens-de-francais-et-obtenir-son-niveau-b1",
    // The title to display in the article page (h1). Less than 60 characters. It's also used to generate the meta title.
    title: "Se préparer aux examens de français et obtenir son niveau B1",
    // The description of the article to display in the article page. Up to 160 characters. It's also used to generate the meta description.
    description:
      "Passer un examen de français peut sembler intimidant, surtout lorsqu'il s'agit de certificats officiels comme le DELF, DALF ou le TEF. Que vous soyez étudiant, professionnel ou simplement passionné par la langue, obtenir un niveau B1 ouvre des portes à de nombreuses opportunités, notamment pour les études ou l'immigration. Dans cet article, nous vous donnons des astuces pratiques pour bien se préparer, ainsi que des ressources utiles pour perfectionner votre français.",
    // An array of categories of the article. It's used to generate the category badges, the category filter, and more.
    categories: [
      categories.find((category) => category.slug === categorySlugs.learning),
    ],
    // The author of the article. It's used to generate a link to the author's bio page.
    author: authors.find((author) => author.slug === authorSlugs.fadoua),
    // The date of the article. It's used to generate the meta date.
    publishedAt: "2024-10-13",
    image: {
      // The image to display in <CardArticle /> components.
      src: frWorkbookImg,
      // The relative URL of the same image to use in the Open Graph meta tags & the Schema Markup JSON-LD. It should be the same image as the src above.
      urlRelative: "/blog2/article3/fr.jpg",
      alt: "Fluent City French",
    },
    locale: "fr_FR",
    // The actual content of the article that will be shown under the <h1> title in the article page.
    content: <Article3 />,
  },
  {
    // The unique slug to use in the URL. It's also used to generate the canonical URL.
    slug: "b1-level-english-exam-preparation-tips-materials-and-strategies-to-succeed",
    // The title to display in the article page (h1). Less than 60 characters. It's also used to generate the meta title.
    title: "B1 Level English Exam Preparation – Tips, Materials, and Strategies to Succeed",
    // The description of the article to display in the article page. Up to 160 characters. It's also used to generate the meta description.
    description:
      "Taking an English exam can seem daunting, especially when it comes to official certificates such as IELTS, TOEFL, TOEIC or CELPIP. Whether you're a student, professional or simply passionate about the language, achieving a B1 level opens doors to many opportunities, including study or immigration. In this article, we give you practical tips on how to prepare for the exam, as well as useful resources for perfecting your English.",
    // An array of categories of the article. It's used to generate the category badges, the category filter, and more.
    categories: [
      categories.find((category) => category.slug === categorySlugs.learning),
    ],
    // The author of the article. It's used to generate a link to the author's bio page.
    author: authors.find((author) => author.slug === authorSlugs.fadoua),
    // The date of the article. It's used to generate the meta date.
    publishedAt: "2025-01-10",
    image: {
      // The image to display in <CardArticle /> components.
      src: enWorkbookImg,
      // The relative URL of the same image to use in the Open Graph meta tags & the Schema Markup JSON-LD. It should be the same image as the src above.
      urlRelative: "/blog2/article4/en.jpg",
      alt: "Fluent City English",
    },
    locale: "en_EN",
    // The actual content of the article that will be shown under the <h1> title in the article page.
    content: <Article4 />,
  },
]